import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { environment } from 'environments/environment';
import { Celebration } from '../model/celebration.type';
import { isCelebrationExpired } from '../utils/celebration.util';

/**
 * Holds the user's active celebration until a page is ready to show it. The back office owns
 * when a celebration exists; this service only fetches it and reports it as closed.
 */
@Injectable({ providedIn: 'root' })
export class CelebrationService {
  private readonly http = inject(HttpClient);

  private readonly baseUrl = `${environment.url.api}/celebration`;

  private readonly pending = signal<Celebration | null>(null);
  readonly pendingCelebration = this.pending.asReadonly();

  /** Closed this session, so a close call that failed or a fetch still in flight can't bring one back. */
  private readonly closedIds = new Set<string>();

  refresh(): void {
    this.http.get<Celebration | null>(this.baseUrl).subscribe({
      next: (celebration) => this.pending.set(this.isShowable(celebration) ? celebration : null),
      // A celebration is never worth an error message; the next refresh tries again.
      error: () => undefined,
    });
  }

  close(id: string): void {
    this.closedIds.add(id);
    if (this.pending()?.id === id) {
      this.pending.set(null);
    }
    this.http.post<void>(`${this.baseUrl}/${id}/close`, {}).subscribe({ error: () => undefined });
  }

  clear(): void {
    this.pending.set(null);
    this.closedIds.clear();
  }

  private isShowable(celebration: Celebration | null): boolean {
    return !!celebration && !this.closedIds.has(celebration.id) && !isCelebrationExpired(celebration);
  }
}
